import { observer } from '@formily/react';
import { isVacuous } from '@yimoka/shared';
import { Typography } from 'antd';
import { LinkProps as ALinkProps } from 'antd/lib/typography/Link';
import React, { useMemo } from 'react';
import { LinkProps as RLinkProps, useHref, useInRouterContext, useLinkClickHandler } from 'react-router-dom';

const { Link: ALink } = Typography;

export type LinkProps = Partial<Pick<RLinkProps, 'to' | 'reloadDocument' | 'replace' | 'state'>> & ALinkProps & React.RefAttributes<HTMLElement>;
type LinkAdapterProps = LinkProps & { to: RLinkProps['to'] };

/** Link 统一外链、站内路由与同页锚点，同时保留各自原生导航语义。 */
export const Link = observer((props: LinkProps) => {
  const inRouterContext = useInRouterContext();
  const { href, to, target, ...args } = props;

  const curTo = useMemo(() => {
    const val = to ?? href;
    if (typeof val === 'string') {
      return val;
    }
    if (isVacuous(val)) {
      return '';
    }
    const { pathname = '', search = '', hash = '' } = val;
    return pathname + search + hash;
  }, [to, href]);

  const isExternal = useMemo(() => curTo && /^[\w]+:\/\//.test(curTo), [curTo]);

  const curTarget = useMemo(() => {
    if (target) {
      return target;
    }
    return isExternal ? '_blank' : undefined;
  }, [isExternal, target]);

  // 只有 href 表达的纯 hash 才交给浏览器；显式 to 仍需由 Router 保留 replace/state 等路由语义。
  if (curTo.startsWith('#') && to === undefined) {
    return <ALink {...args} href={curTo} target={curTarget} />;
  }

  if (!isExternal) {
    if (!inRouterContext) {
      return (
        <ALink
          {...args}
          href={curTo}
          target={curTarget}
        />
      );
    }
    return (<LinkAdapter {...args} target={curTarget} to={curTo} />);
  }

  return (
    <ALink
      rel="noopener noreferrer"
      {...args}
      href={curTo}
      target={curTarget}
    />
  );
});

/** LinkAdapter 仅承接需要 React Router 保留 SPA 状态的站内路径导航。 */
export const LinkAdapter = (props: LinkAdapterProps) => {
  const { onClick, replace = false, state, target, to, type, ...args } = props;
  const href = useHref(to);
  const handleClick = useLinkClickHandler<HTMLElement>(to, { replace, state, target });

  return (
    <ALink
      {...args}
      href={href}
      target={target}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          handleClick(event);
        }
      }}
    />
  );
};
